// articles/BestBallSetup.js
// Event setup guide #2 (Sept 27, 2026): a Best Ball team event in TTO, start to
// finish, in the order the steps have to happen. In TTO "Best Ball" means every
// player plays his own ball and enters his own score, and the team counts its best
// score (or best 2 or 3) on each hole. Written from the live screens — button and
// tab names are quoted exactly as they appear, so if a label changes in App.js,
// change it here too.
//
// Linked from inside the app: the guides chooser (📖 Guide → Setting up Events /
// Tournaments), the Event setup guides buttons, and the Best Ball panel in Games
// Setup. Public, so prospective organizers can read it before they sign up.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS, CtpSection } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-best-ball-golf-tournament');

const FAQS = [
  {
    q: 'What is the difference between Best Ball and Captain’s Choice?',
    a: 'In Best Ball every player plays his own ball from tee to hole and enters his own score; the team counts its best score (or best 2 or 3) on each hole. In a Captain’s Choice the team picks one shot each time and records one team score. TTO sets them up differently, so each has its own guide.',
  },
  {
    q: 'Do Best Ball scores post to GHIN?',
    a: 'Yes. Each player plays his own ball, so his round is acceptable for handicap purposes. Players post their own scores; TTO never posts for them. A shamble is different: its scores are not acceptable for handicap purposes, so saving a Shamble game turns GHIN posting off for the round.',
  },
  {
    q: 'Can I run a shamble?',
    a: 'Yes. Tick Shamble instead of Best Ball on the Games card. Everyone drives, the team picks the best drive, and each player plays his own ball in. It has its own step-by-step guide: How to Set Up a Shamble Golf Tournament.',
  },
  {
    q: 'What handicap does each player get?',
    a: 'There is no team handicap. Each player plays off a percentage of his own course handicap from his own tee: the USGA recommendations are 85% for 2-man best ball and 75%, 85% or 100% for best 1, 2 or 3 of 4. 3-man has no USGA figure; TTO starts it at 75% for best 1 of 3 and 85% for best 2 of 3. You can change the percentage, or set a player’s handicap for this event only with Event handicaps.',
  },
  {
    q: 'What happens if a 4-man team is a player short?',
    a: 'TTO draws a blind automatically when the round starts: a player picked at random from the missing player’s handicap band, whose scores fill the empty spot. Only you see who it is until the round is final. A 2-man or 3-man team is never given a blind.',
  },
  {
    q: 'What if a player picks up on a hole?',
    a: 'He enters an X. It counts as a net double bogey and shows as “7x”. In a 4-man best ball, especially with a blind in play, remind players to hole out.',
  },
  {
    q: 'Can I pay the front nine and back nine separately?',
    a: 'Yes. In What it pays, choose Front & back, or Front, back & overall. Each part is its own competition with its own share of the buy-in and its own paid places. For example, a $16 buy-in split $8 / $8 and paying 75% / 25% on each nine gives a 20-player field $120 and $40 on the front and the same on the back, which a foursome splits $30 and $10 each. Each player keeps his 18-hole strokes where they fall, the game’s standings get Front 9 / Back 9 / Overall tabs, and the statement lists each part’s winners with one total per player. It needs an 18-hole round ranked on Net or Gross, not Both.',
  },
  {
    q: 'Does TTO handle the money?',
    a: 'Only if you want it to. Choose Handled outside TTO and TTO names the places while you pay from your own prize table. Choose Winner takes all or Paid places with a buy-in and TTO settles it player to player, with teammates splitting their team’s share evenly.',
  },
];

export default function BestBallSetup() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Best Ball Golf Tournament
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Event setup guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        In a Best Ball tournament every player plays his own ball and records his own score, and
        the team counts its best score on each hole: the better of two in a 2-man best ball, or the
        best 1, 2 or 3 scores of a foursome. This guide walks you through setting up a Best Ball golf
        tournament in Tee Time Organizer, from the first click to the final results, in the order
        the steps need to happen.
      </P>

      <StepTracker
        storageKey="tto_guide_best_ball_steps"
        steps={[
          'Create the event',
          'Set up the event',
          'Invite players',
          'Starting holes & teams',
          'Best Ball game',
          'Handicaps for the day',
          'Print cards & signs',
          'Play & leaderboard',
          'Cards, ties & finalize',
        ]}
        note="Steps 1 to 3 can be done weeks ahead; steps 4 to 7 wait until the field is set. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <P>Settle these decisions first. Each one is entered in a later step, but you&rsquo;ll need the answers there.</P>
      <UL>
        <LI>
          <strong>Each event needs an Event Pass</strong> (unless events are included in your
          plan). Where the <Btn>This is an event</Btn> checkbox would be, you&rsquo;ll see{' '}
          <Btn>🔒 This is an event · Get an Event Pass</Btn>. Buy the pass there and the checkbox
          opens; the pass also switches on scoring and the event games. Running your very first
          event? Start with{' '}
          <a href="/guides/how-to-run-a-golf-tournament-with-tee-time-organizer" style={{ color: colors.green700 }}>
            Setting Up Your Tournament
          </a>.
        </LI>
        <LI>
          <strong>Who is invited:</strong> your whole player list, or only the players you choose
          (step 3).
        </LI>
        <LI>
          <strong>Shotgun or tee times</strong>, and for a shotgun, how many teams start on each hole
          (step 4).
        </LI>
        <LI>
          <strong>The format</strong> (step 5): 2-man best ball, a threesome counting its best 1 or 2
          scores, or a foursome counting its best 1, 2 or 3 scores on each hole.
        </LI>
        <LI>
          <strong>Net, gross or both</strong>, and the handicap allowance (step 5).
        </LI>
        <LI>
          <strong>Money:</strong> whether TTO settles a buy-in or you pay prizes yourself, how many
          places you pay, and how ties are broken. TTO decides ties only for the places you pay, so it
          needs this before scores come in (step 5).
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Create the tee time and mark it as an event" when="Any time before registration opens">
        <UL>
          <LI>
            On your dashboard, in <Btn>Create Tee Time</Btn>, fill in the date, time, course and
            slots.
          </LI>
          <LI>
            <strong>Start Type:</strong> choose <Btn>Shotgun</Btn> if all teams start at once. You
            assign the starting holes later, on the Pairings page (step 4).
          </LI>
          <LI>
            <strong>Inviting only selected players?</strong> Tick <Btn>Private Tee Time</Btn> (see
            step 3 for why).
          </LI>
          <LI>
            Tick <Btn>This is an event</Btn> and click <Btn>Add Tee Time</Btn>. The checkbox appears
            for single tee times, not recurring or bulk ones.
          </LI>
          <LI>
            The <Btn>Set up event</Btn> window opens on its own. For an existing tee time, use{' '}
            <Btn>✏️ Edit</Btn>, tick <Btn>This is an event</Btn>, then <Btn>Set up event ▸</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Set up the event" when="Right after step 1">
        <UL>
          <LI>
            <strong>Event name</strong> (the only required field). It becomes the title on the
            leaderboard, scorecards and cart signs.
          </LI>
          <LI>
            <strong>Live leaderboard address</strong>: made from the name; shorten it if you like.
            It&rsquo;s printed on the cards, so settle it before you print.
          </LI>
          <LI>
            <strong>Event logo</strong> (optional): shown on the leaderboard, scorecards and cart
            signs.
          </LI>
          <LI>
            <strong>Scoring</strong>: leave it on <Btn>Each player enters their own score</Btn>. Best
            Ball needs every player&rsquo;s own score, so it can&rsquo;t run on a round set to one
            score per team.
          </LI>
          <LI>
            <strong>GHIN posting eligible</strong>: leave it on <strong>Yes</strong>. Each player
            plays his own ball, so players can post their rounds.
          </LI>
          <LI>
            Click <Btn>Save event</Btn>. If you came in through <Btn>✏️ Edit</Btn>, also click{' '}
            <Btn>Save Changes</Btn> on the tee time.
          </LI>
        </UL>
        <Heads>
          <strong>Money, places paid and tie-breaking are not on this screen.</strong> You set them
          with the game in step 5. Have your payout plan decided by then.
        </Heads>
      </Step>

      <Step n={3} title="Invite players" when="When you're ready to open registration">
        <P>
          <Btn>📣 Publish</Btn> on the tee time card makes the event live. How you publish decides who
          hears about it.
        </P>
        <H3>Option A: invite your whole player list</H3>
        <UL>
          <LI>
            <Btn>📣 Publish</Btn> sends a notice to every player on your list, and the event appears in
            each player&rsquo;s app, where they can RSVP.
          </LI>
          <LI>
            Choosing <Btn>Send No Notifications</Btn> skips the notice, but the event still appears in
            your players&rsquo; apps and they can still RSVP.
          </LI>
        </UL>
        <H3>Option B: invite only the players you choose (recommended)</H3>
        <UL>
          <LI>
            Make sure the tee time is marked <Btn>Private Tee Time</Btn>. A private event does not
            appear in your players&rsquo; apps unless they have been invited and have accepted the
            invitation to play. Everyone playing sees it; the rest of your list doesn&rsquo;t.
          </LI>
          <LI>
            <Btn>📣 Publish</Btn> it with <Btn>Send No Notifications</Btn>, which gives{' '}
            <Btn>Publish (No Notifications)</Btn>.
          </LI>
          <LI>
            Click <Btn>🎟️ Invite</Btn> on the tee time card and copy the invite link.
          </LI>
          <LI>
            Put the link in an invitation email you write yourself and send it to the players you
            want.
          </LI>
          <LI>
            A player who opens the link is registered. Anyone who isn&rsquo;t already in your TTO
            player list is added as a guest.
          </LI>
        </UL>
        <P>Registration is individual. You form the teams in step 4.</P>
      </Step>

      <Step n={4} title="Set the starting holes and build the teams" when="Once the field is set, on the Pairings page">
        <Heads>
          <strong>Order matters.</strong> For a shotgun, set up the starting holes <em>first</em>,
          then fill them with teams. Each saved pairing group <em>is</em> a team: the game finds its
          teams there, and nothing counts until pairings are saved.
        </Heads>

        <H3>How teams map to groups</H3>
        <UL>
          <LI>
            <strong>2-man best ball:</strong> pair the field as twosomes. Two twosomes share a hole or
            a tee time (for example 1A and 1B), and each twosome is a team.
          </LI>
          <LI>
            <strong>Best 1 or 2 of 3:</strong> pair the field as threesomes. Each threesome is a team,
            and no blind is drawn.
          </LI>
          <LI>
            <strong>Best 1, 2 or 3 of 4:</strong> pair the field as foursomes. Each foursome is a team.
            A foursome that is one player short is fine; TTO draws a blind for it (see step 8).
          </LI>
        </UL>

        <H3>4a. Set up the shotgun (skip for a tee-times start)</H3>
        <UL>
          <LI>
            Open the <strong>Pairings</strong> page for the event and turn on{' '}
            <Btn>🎯 Shotgun Start</Btn>. The <strong>⚡ Shotgun Setup</strong> window opens. Later
            you can reopen it with <Btn>⚡ Edit Setup</Btn>.
          </LI>
          <LI>
            <strong>Group size:</strong> the number of players in each starting group. For a 2-man
            best ball with two twosomes on a hole, choose 2 and put two groups on each hole.
          </LI>
          <LI>
            <strong>Presets</strong> fill in the list of starting holes:
            <UL>
              <LI>
                <Btn>Standard (18)</Btn>: one group on every hole. Use it for a full field.
              </LI>
              <LI>
                <Btn>Modified (9)</Btn>: one group on each of holes 1 to 9.
              </LI>
              <LI>
                <Btn>Fit to players</Btn>: only as many groups as your field needs, filled from hole
                1 and then backwards (18, 17, 16 …). Hole 1 clears quickly and everyone finishes near
                the clubhouse. This is the usual choice for a partial field.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Adjust by hand:</strong> use <strong>+</strong> and <strong>−</strong> on a hole
            to change its number of groups. Two groups on one hole start as A and B (for example 1A
            and 1B). <strong>✕</strong> removes a hole and <Btn>+ Add Hole</Btn> puts one back.
          </LI>
          <LI>
            Check the capacity line. It should read <strong>✓ Fits N players</strong>, not{' '}
            <strong>over capacity</strong>. Then click <Btn>Create N Groups</Btn>.
          </LI>
        </UL>

        <H3>4b. Fill the teams</H3>
        <UL>
          <LI>
            <strong>Shotgun:</strong> the green <strong>Shotgun Structure</strong> bar has fill
            buttons:
            <UL>
              <LI>
                <strong>Foursomes:</strong> <Btn>⚖️ Balanced</Btn>, <Btn>🎲 Random</Btn> or{' '}
                <Btn>⛳ HCP</Btn>.
              </LI>
              <LI>
                <strong>Twosomes:</strong> <Btn>⚖️ Balanced</Btn> or <Btn>🎲 Random</Btn>.
              </LI>
            </UL>
            <strong>Balanced</strong> spreads the handicaps so each team has a fair mix. Twosomes need
            an even number of players.
          </LI>
          <LI>
            <strong>Tee-times start:</strong> use <Btn>⚖️ Balanced Flights ▾</Btn> &rarr; Balanced
            Twosomes or Balanced Foursomes.
          </LI>
          <LI>
            <strong>Teams that signed up together:</strong> move the players into the same group by
            hand.
          </LI>
          <LI>
            Click <Btn>💾 Save Pairings</Btn> to save the teams and show the pairings in each
            player&rsquo;s app. Use{' '}
            <Btn>Save without sharing</Btn> to hold them back, for example until a reception the
            night before.
          </LI>
        </UL>
      </Step>

      <Step n={5} title="Add the Best Ball game" when="After pairings are saved">
        <Heads>
          <strong>Every player needs a complete profile before the round.</strong> Each player&rsquo;s
          profile must include a <strong>preferred tee</strong> and a <strong>Handicap Index</strong>,
          so that his course handicap can be determined.
          Each player gets his strokes from his own course handicap on his own tee. Check them in
          Admin &rarr; Players &rarr; edit each player, including guests added from your invite link.
          You can add or change them any time up to the start of the event, but they must be filled
          in by then. In a net game, a team with a player who has no handicap is not ranked.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Best Ball</strong>.
          </LI>
          <LI>
            <strong>Format:</strong> <Btn>2-man best ball</Btn>, <Btn>Best 1 of 3</Btn>,{' '}
            <Btn>Best 2 of 3</Btn>, <Btn>Best 1 of 4</Btn>, <Btn>Best 2 of 4</Btn>,{' '}
            <Btn>Best 3 of 4</Btn> or <Btn>One gross + one net</Btn>. The number of scores that count is the
            same on every hole. If the format doesn&rsquo;t match your group size (two threesomes on a
            4-man format, say), the Games card warns you.
          </LI>
          <LI>
            <strong>One gross + one net</strong> (foursomes): on each hole the team counts one
            player's gross score plus a <em>different</em> player's net score, the pair with the
            lowest total. TTO picks the pair. The net ball starts at 100% of course handicap.
          </LI>
          <LI>
            <strong>Rank on:</strong> Net, Gross or Both. (One gross + one net has a single team
            score, so it skips this.)
          </LI>
          <LI>
            <strong>Handicap allowance:</strong> filled in from the USGA recommendations for the
            format (2-man 85%; best 1, 2 or 3 of 4: 75%, 85%, 100%; best 1 or 2 of 3, which has no USGA
            figure: 75%, 85%). There is no team handicap: each
            player plays off that percentage of his own course handicap. Change it if your event uses
            a different one; <strong>Reset</strong> puts it back.
          </LI>
          <LI>
            <strong>What it pays:</strong> <Btn>Whole round</Btn>, <Btn>Front &amp; back</Btn> or{' '}
            <Btn>Front, back &amp; overall</Btn>. With the nines paid separately, each part is its own
            competition with its own places and money: say how the buy-in is split between the parts
            and, for paid places, the percentages for each. The window shows what each place pays for
            today&rsquo;s field (for example $16 split $8 / $8, 75% / 25%, 20 players: $120 and $40 on
            each nine, which a foursome splits $30 and $10 each). Each player keeps his 18-hole
            strokes where they fall, and the game&rsquo;s standings in the app get Front 9 / Back 9 / Overall tabs. It needs
            an 18-hole round ranked on Net or Gross (not Both).
          </LI>
          <LI>
            <strong>Payouts:</strong>
            <UL>
              <LI>
                <strong>Handled outside TTO</strong> (the usual choice for an event): TTO names the
                places and you pay from your own prize table. Set <strong>Places decided</strong> to
                the number of places you pay.
              </LI>
              <LI>
                <strong>Winner takes all</strong> or <strong>Paid places</strong>: enter the{' '}
                <strong>Buy-in</strong> per player. For paid places, set the percentages for each
                place; they must add up to 100. Every player pays the buy-in and teammates split
                their team&rsquo;s winnings evenly.
                <div style={{ marginTop: 6 }}>
                  <strong>Keeping part of the entry as a fee?</strong> TTO pays out the whole
                  buy-in, so choose <strong>Handled outside TTO</strong> and pay the prizes
                  yourself.
                </div>
              </LI>
              <LI>
                <strong>Money on a tie:</strong> tied scores share the money, or pay by the decided
                order.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>The blind</strong> (4-man formats with a buy-in). A foursome that is one player
            short gets a <em>blind</em>: a player drawn from the field whose scores fill the empty
            spot (see <a href="#step-8" style={{ color: colors.green700, fontWeight: 600 }}>The blind
            draw</a> in step 8). Choose{' '}
            <strong>Blind is paid</strong> (the blind takes a player&rsquo;s share if his adopted team
            wins money) or <strong>Blind is not paid</strong> (the team&rsquo;s three players split
            its whole share).
          </LI>
          <LI>
            <strong>How ties are decided:</strong>
            <UL>
              <LI>
                <strong>Scorecard playoff (USGA):</strong> the best team score over the last 9 holes,
                then the last 6, the last 3, then the 18th.
              </LI>
              <LI>
                <strong>Hole handicap:</strong> the hardest holes first.
              </LI>
              <LI>
                <strong>We&rsquo;ll decide:</strong> you put the tied teams in order yourself, for
                example after a real playoff.
              </LI>
            </UL>
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>. The <strong>Teams — from Pairings</strong> list shows each
            team, its players with <strong>✓ In</strong> / <strong>— Out</strong>, and any team that
            is short or has too many players.
          </LI>
        </UL>
        <Heads>
          <strong>Changed the pairings?</strong> Open the game and save again before the first score
          is entered, so the teams refresh. Once scoring starts the teams are fixed; don&rsquo;t
          re-pair mid-round.
        </Heads>
        <CtpSection />
      </Step>

      <Step n={6} title="Adjust handicaps for the day (optional)" when="Any time before or during the round">
        <UL>
          <LI>
            If the committee sets a player&rsquo;s handicap for this event only, use{' '}
            <strong>⚖️ Event handicaps</strong> in Admin &rarr; <strong>⛳ Scoring</strong> &rarr;{' '}
            <strong>Post-Round Score Entry</strong>. The player&rsquo;s club record and GHIN are not
            changed.
          </LI>
          <LI>
            A change made during the round re-scores the holes he has already played, so the
            leaderboard may move.
          </LI>
        </UL>
      </Step>

      <Step n={7} title="Print scorecards and cart signs" when="The day before or the morning of">
        <UL>
          <LI>
            In <Btn>🖨️ Print</Btn>: <Btn>Print scorecards</Btn> and <Btn>Print cart signs</Btn>. The
            tee sheet, event sign and posting sheets are there too.
          </LI>
          <LI>
            On a net game each player&rsquo;s card is marked with an asterisk on every hole where he
            gets a stroke, and shows his playing handicap (&ldquo;PH&rdquo;) at the game&rsquo;s
            allowance.
          </LI>
          <LI>
            Cards for a 4-man format print <strong>ALL PLAYERS MUST HOLE OUT</strong>.
          </LI>
          <LI>
            <strong>Scorecard codes:</strong> before you print, open <Btn>Scorecard codes…</Btn> in{' '}
            <Btn>🖨️ Print</Btn> and click <Btn>Issue codes</Btn>. Each group&rsquo;s card and cart
            sign then carries a QR code and a six-digit code (step 8).
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={8} title="Play and follow the leaderboard" when="During the round">
        <H3>Keeping score</H3>
        <UL>
          <LI>
            Players in your TTO player list get a link to their TTO app in their reminder email. Each
            player enters his own score on each hole.
          </LI>
          <LI>
            <strong>No link handy, or a guest?</strong> He points his phone&rsquo;s camera at the QR
            code on his group&rsquo;s scorecard or cart sign, taps his own name, and his card opens.
            No app, password or sign-in is needed. Posting to GHIN still needs his own TTO link or
            ghin.com.
          </LI>
          <LI>
            One player can keep score for his whole group with <strong>+ Score for another player</strong>.
            If someone stops playing, he taps <strong>Edit players</strong> and unticks him.
          </LI>
          <LI>
            <strong>Picked up?</strong> A player taps <strong>X</strong>. It counts as a net double
            bogey and shows as &ldquo;7x&rdquo;. TTO won&rsquo;t move to the next hole until every
            player on the card has a score or an X.
          </LI>
          <LI>
            In a 4-man format the app reminds players that all players must hole out.
          </LI>
        </UL>

        <H3>The blind draw (4-man formats)</H3>
        <UL>
          <LI>
            <strong>Automatic.</strong> When the first score of the round is entered, TTO draws a
            blind for each foursome that is a player short. The blind is picked at random from the
            missing player&rsquo;s handicap band, never from the short team, and his scores fill the
            empty spot.
          </LI>
          <LI>
            <strong>Who knows:</strong> only you (and anyone at the event desk) see the blind&rsquo;s
            name, on the Games card and the Pairings page. Players and the leaderboard see
            &ldquo;+ Blind Draw&rdquo; until the round is final. The drawn player learns it from his
            results email.
          </LI>
          <LI>
            <strong>A player withdraws mid-round:</strong> mark him <strong>— Out</strong> on the Games
            card. His holes stand, and a blind covers the rest.
          </LI>
          <LI>
            <strong>A late arrival:</strong> put him back <strong>✓ In</strong> and the blind is
            removed, but only until his team has entered its first score. After that the blind stays.
          </LI>
          <LI>2-man and 3-man teams never get a blind.</LI>
        </UL>

        <H3>Leaderboard and helpers</H3>
        <UL>
          <LI>
            The leaderboard shows one row per team. A hole counts for a team once every player on it
            has scored the hole, so a team may show &ldquo;waiting on&rdquo; a player.
          </LI>
          <LI>
            An event&rsquo;s leaderboard is shared automatically at its address. For the clubhouse TV,
            open <strong>📺 TV view</strong> from the Live Leaderboard box on the Scoring tab (or{' '}
            <Btn>📺 TV</Btn> on the tee time card).
          </LI>
          <LI>
            To let pro shop staff run the desk for this one event, without access to your dashboard,
            create an event desk code in <Btn>Set up event</Btn> &rarr;{' '}
            <strong>Event desk access</strong>.
          </LI>
        </UL>
      </Step>

      <Step n={9} title="Check the cards, settle ties and finalize" when="When the last card is in">
        <H3>When the cards come in</H3>
        <UL>
          <LI>
            Check each player&rsquo;s signed card against what is in TTO. Scores entered on phones
            during the round are already there.
          </LI>
          <LI>
            Open Admin &rarr; <strong>⛳ Scoring</strong> &rarr; <strong>⛳ Post-Round Score Entry</strong>{' '}
            and choose the round. Each player has a tab, listed by last name, and his grid shows
            every hole already entered.
          </LI>
          <LI>
            <strong>To correct a hole</strong>, click it and type the right score, or X for a pick-up
            (it shows as &ldquo;7x&rdquo;, a net double bogey). Then click{' '}
            <Btn>Save [player]&rsquo;s Scores</Btn>, or <Btn>Save All Scores</Btn> after several.
            The leaderboard updates straight away.
          </LI>
          <LI>
            <strong>A player who didn&rsquo;t score on his phone:</strong> enter his whole card the
            same way.
          </LI>
          <LI>
            A blank hole <em>between</em> holes he played won&rsquo;t save: enter a score or an X.
            Blank holes after he stopped are fine; TTO asks about him when you finalize.
          </LI>
          <LI>
            <strong>Already finalized?</strong> Click <Btn>↶ Reopen Round</Btn> in{' '}
            <Btn>🎮 Games</Btn> first, fix the card, then finalize again. Otherwise the results and
            any money don&rsquo;t change.
          </LI>
          <LI>
            Helpers at the event desk can do the same from its <strong>Scores</strong> tile.
          </LI>
        </UL>

        <H3>Ties and finalizing</H3>
        <UL>
          <LI>
            With <strong>Scorecard playoff</strong> or <strong>Hole handicap</strong>, TTO breaks ties
            for the places you pay on its own, and the leaderboard marks them.
          </LI>
          <LI>
            With <strong>We&rsquo;ll decide</strong>, or after a real playoff, open{' '}
            <Btn>🎮 Games</Btn>, put the tied teams in order with ▲ ▼, click{' '}
            <Btn>Use this order</Btn>, and save.
          </LI>
          <LI>
            Click <Btn>🏁 Finalize Round &amp; Save Settlements</Btn>. If a card is incomplete, TTO
            lists the player and lets you mark him DNF or wait for his scores.
          </LI>
          <LI>
            With a buy-in, TTO settles the money player to player and you can email each player his
            results. With Handled outside TTO, the results show the places only. Need a correction?{' '}
            <Btn>↶ Reopen Round</Btn>, fix it, and finalize again.
          </LI>
        </UL>
      </Step>

      <div id="shamble" style={{ scrollMarginTop: 16 }}>
        <H2>Running a Shamble?</H2>
      </div>
      <P>
        A shamble is set up the same way, with the Shamble game instead of Best Ball: its own
        handicap allowances, a drive rule on the cards, and GHIN posting off. It has its own guide:{' '}
        <a href="/guides/how-to-set-up-a-shamble-golf-tournament" style={{ color: colors.green700, fontWeight: 600 }}>
          How to Set Up a Shamble Golf Tournament
        </a>.
      </P>

      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>The teams are fixed.</strong> They stop refreshing from the Pairings page, and
          blinds are drawn for short foursomes.
        </LI>
        <LI>
          <strong>A blind is final</strong> once his team has entered a score.
        </LI>
        <LI>
          <strong>Game settings can still change</strong>, but change them only if you must: the
          leaderboard and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="best-ball-setup" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
